import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  ArrowLeft,
  Users,
  Building2,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Star,
  MapPin,
  Loader2,
  BarChart3,
  PieChart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const { token, loading: authLoading } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchAnalytics();
    }
  }, [token]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive mb-4">{error}</p>
          <Link to="/map">
            <Button variant="outline">Back to Map</Button>
          </Link>
        </div>
      </div>
    );
  }

  const ratingData = analytics?.rating_distribution || {};
  const totalRatings = Object.values(ratingData).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      {/* Header */}
      <header className="h-16 bg-card border-b flex items-center justify-between px-4 lg:px-6 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-display font-bold text-primary">Analytics Dashboard</h1>
              <p className="text-xs text-muted-foreground">Community insights & demographics</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{analytics?.total_users || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Community members</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Businesses</CardTitle>
              <Building2 className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{analytics?.total_businesses || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">On the map</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{analytics?.total_reviews || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Community experiences</p>
            </CardContent>
          </Card>
        </div>

        {/* Demographics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-primary" />
                Demographics Breakdown
              </CardTitle>
              <CardDescription>How our community identifies</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(analytics?.demographics_breakdown || {})
                  .filter(([key]) => key !== 'zip_codes')
                  .sort((a, b) => b[1] - a[1])
                  .map(([race, count]) => {
                    const percentage = analytics?.total_users > 0 
                      ? ((count / analytics.total_users) * 100).toFixed(1) 
                      : 0;
                    return (
                      <div key={race} className="flex items-center justify-between">
                        <span className="text-sm">{race}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-16 text-right">
                            {count} ({percentage}%)
                          </span>
                        </div>
                      </div>
                    );
                  })}
                {Object.keys(analytics?.demographics_breakdown || {}).filter(k => k !== 'zip_codes').length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No demographic data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Rating Distribution
              </CardTitle>
              <CardDescription>How businesses are rated</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = ratingData[String(rating)] || 0;
                  const percentage = totalRatings > 0 ? ((count / totalRatings) * 100).toFixed(1) : 0;
                  const colors = {
                    5: 'bg-green-500',
                    4: 'bg-green-400',
                    3: 'bg-yellow-500',
                    2: 'bg-orange-500',
                    1: 'bg-red-500'
                  };
                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-16">
                        <span className="text-sm font-medium">{rating}</span>
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      </div>
                      <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors[rating]} rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-20 text-right">
                        {count} ({percentage}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reviews by Location */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Reviews by Neighborhood
              </CardTitle>
              <CardDescription>Most reviewed areas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(analytics?.reviews_by_neighborhood || {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([neighborhood, count]) => (
                    <div key={neighborhood} className="flex items-center justify-between py-1">
                      <span className="text-sm">{neighborhood}</span>
                      <span className="text-sm font-medium text-primary">{count} reviews</span>
                    </div>
                  ))}
                {Object.keys(analytics?.reviews_by_neighborhood || {}).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No reviews yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Reviews by Category
              </CardTitle>
              <CardDescription>Most reviewed business types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(analytics?.reviews_by_category || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between py-1">
                      <span className="text-sm">{category}</span>
                      <span className="text-sm font-medium text-primary">{count} reviews</span>
                    </div>
                  ))}
                {Object.keys(analytics?.reviews_by_category || {}).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No reviews yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Business Rankings */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <TrendingUp className="w-5 h-5" />
                Top Rated
              </CardTitle>
              <CardDescription>Highest rated businesses (3+ reviews)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(analytics?.top_rated_businesses || []).map((biz, idx) => (
                  <div key={biz.id} className="flex items-start gap-3">
                    <span className="text-lg font-bold text-muted-foreground">#{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{biz.name}</p>
                      <p className="text-xs text-muted-foreground">{biz.neighborhood}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs font-medium">{biz.avg_rating?.toFixed(1)}</span>
                        <span className="text-xs text-muted-foreground">({biz.review_count})</span>
                      </div>
                    </div>
                  </div>
                ))}
                {(analytics?.top_rated_businesses || []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">Need more reviews</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600">
                <MessageSquare className="w-5 h-5" />
                Most Reviewed
              </CardTitle>
              <CardDescription>Businesses with the most feedback</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(analytics?.most_reviewed_businesses || []).map((biz, idx) => (
                  <div key={biz.id} className="flex items-start gap-3">
                    <span className="text-lg font-bold text-muted-foreground">#{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{biz.name}</p>
                      <p className="text-xs text-muted-foreground">{biz.neighborhood}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <MessageSquare className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs font-medium">{biz.review_count} reviews</span>
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 ml-2" />
                        <span className="text-xs">{biz.avg_rating?.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {(analytics?.most_reviewed_businesses || []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No reviews yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Flagged Businesses
              </CardTitle>
              <CardDescription>Businesses with 2+ low ratings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(analytics?.flagged_businesses || []).map((biz, idx) => (
                  <div key={biz.id} className="flex items-start gap-3">
                    <span className="text-lg font-bold text-red-500">#{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{biz.name}</p>
                      <p className="text-xs text-muted-foreground">{biz.neighborhood}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <TrendingDown className="w-3 h-3 text-red-500" />
                        <span className="text-xs text-red-600">{biz.low_rating_count} low ratings</span>
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 ml-2" />
                        <span className="text-xs">{biz.avg_rating?.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {(analytics?.flagged_businesses || []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No flagged businesses</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
